import { useQuery } from '@tanstack/react-query'

import type { VacationType } from '@czas/shared'

import { apiFetch } from '@/lib/api'

export interface MonthlyReportUserPosition {
  id: string
  name: string
  sortOrder: number
}

export interface MonthlyReportUser {
  id: string
  name: string
  email: string
  position: MonthlyReportUserPosition | null
  employmentFraction: number | null
}

export interface MonthlyReportVacationLine {
  type: VacationType
  days: number
  /** Procent pełnego dnia zaliczony temu typowi (L4 = 80, bezpłatny = 0). */
  percent: number
  /** days × dailyHours × percent/100. */
  hours: number
  /** hours × pełna stawka — procent siedzi już w godzinach. */
  pay: number
}

export interface MonthlyReportPayroll {
  hourlyRate: number | null
  /** Pełny dzień pracy: 8h × etat. Baza dla procentów. */
  dailyHours: number
  workHours: number
  workPay: number
  vacationDaysByType: Record<VacationType, number>
  /** Tylko typy z dniami > 0. Kwoty policzone przez backend — nie licz ich sam. */
  vacationLines: MonthlyReportVacationLine[]
  vacationHours: number
  vacationPay: number
  total: number
}

export interface MonthlyReportVacationSegment {
  type: VacationType
  firstDay: string
  lastDay: string
  days: number
  hours: number
}

export interface MonthlyReportAbsenceDay {
  /** YYYY-MM-DD — dzień roboczy nieobecności, po deduplikacji. */
  date: string
  type: VacationType
  /** Procent pełnego dnia zaliczony temu typowi (L4 = 80, bezpłatny = 0). */
  percent: number
  /** Godziny zaliczone TEGO dnia: 8h × etat × percent/100. */
  hours: number
}

export interface MonthlyReportVacationBalance {
  year: number
  totalDays: number
  usedDays: number
  remainingDays: number
}

export interface MonthlyReportSession {
  id: string
  startedAt: string
  endedAt: string | null
  durationMs: number | null
  outsideClinic: boolean
  startedOutside: boolean
  startedDistanceM: number | null
  endedOutside: boolean
  endedDistanceM: number | null
  startedSource: 'EMPLOYEE_APP' | 'ADMIN_ADDED' | 'ADMIN_CORRECTION'
  autoClosed: boolean
}

export interface MonthlyReportUserData {
  user: MonthlyReportUser
  summary: {
    totalHours: number
    daysWorked: number
    outsideClinicCount: number
    /** Dni urlopu zaliczane do czasu pracy (bez bezpłatnego). */
    vacationDays: number
    unpaidLeaveDays: number
    /** Godziny z nieobecności, ważone per typ (L4 = 80% dnia). */
    vacationHours: number
    /** totalHours + vacationHours — podstawa rozliczenia miesiąca. */
    creditedHours: number
    /** false = zatrudniony, ale w tym miesiącu ani sesji, ani urlopu. */
    hasActivity: boolean
  }
  payroll: MonthlyReportPayroll
  vacationBalance: MonthlyReportVacationBalance | null
  /**
   * Dni z puli urlopowej przejęte przez L4 (art. 166 KP) — choroba przerwała
   * urlop, więc wróciły do puli. Saldo już to uwzględnia; liczba służy do
   * wyjaśnienia, czemu saldo różni się od sumy dni z wniosków.
   */
  sickLeaveDisplacedDays: number
  vacationSegments: MonthlyReportVacationSegment[]
  /** Dni nieobecności rozbite na daty — wiersze dziennej tabeli. */
  absences: MonthlyReportAbsenceDay[]
  sessions: MonthlyReportSession[]
}

export interface MonthlyReport {
  yearMonth: string
  team: {
    totalHours: number
    totalVacationHours: number
    totalCreditedHours: number
    totalSessions: number
    daysWithActivity: number
    totalPayroll: number
    employeesReported: number
    employeesWithActivity: number
    totalVacationDaysByType: Record<VacationType, number>
  }
  users: MonthlyReportUserData[]
}

export function useMonthlyReport(yearMonth: string) {
  return useQuery({
    queryKey: ['admin', 'reports', 'monthly', yearMonth],
    queryFn: () =>
      apiFetch<MonthlyReport>(
        `/api/admin/reports/monthly?yearMonth=${yearMonth}`,
      ),
    staleTime: 30_000,
  })
}

async function downloadBlob(url: string, fallbackFilename: string) {
  const response = await fetch(url, { credentials: 'include' })
  if (!response.ok) {
    let msg = `Błąd serwera (${response.status})`
    try {
      const body = await response.json()
      if (typeof body?.error === 'string') msg = body.error
      else if (typeof body?.message === 'string') msg = body.message
    } catch {
      /* binary or empty body */
    }
    throw new Error(msg)
  }
  const disposition = response.headers.get('Content-Disposition') ?? ''
  const filenameMatch = disposition.match(/filename="([^"]+)"/)
  const filename = filenameMatch?.[1] ?? fallbackFilename

  const blob = await response.blob()
  const blobUrl = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = blobUrl
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(blobUrl)
  return filename
}

export function downloadMonthlyReportPdf(yearMonth: string) {
  return downloadBlob(
    `/api/admin/reports/monthly/pdf?yearMonth=${yearMonth}`,
    `raport-czasu-pracy_${yearMonth}.pdf`,
  )
}

export function downloadMonthlyXlsx(yearMonth: string) {
  // Range = first/last day of selected month
  const [y, m] = yearMonth.split('-').map(Number)
  if (!y || !m) throw new Error('Invalid yearMonth')
  const from = `${y}-${String(m).padStart(2, '0')}-01`
  const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate()
  const to = `${y}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`
  return downloadBlob(
    `/api/admin/work-sessions/export?from=${from}&to=${to}`,
    `czas-pracy_zespol_${from}_${to}.xlsx`,
  )
}
